import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiEye } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Toggle, Select } from '../../components/common';
import { blogService } from '../../services/blogService';
import { formatDate } from '../../utils/dateUtils';
import { truncate } from '../../utils/stringUtils';
import { useDebounce } from '../../hooks';

const BlogListPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ open: false, slug: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const pageSize = 10;

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await blogService.getAll({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch,
        category: categoryFilter
      });
      setBlogs(response.data.blogs);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const response = await blogService.getCategories();
      setCategories(response.data.map(cat => ({ value: cat, label: cat })));
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [fetchBlogs]);

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleToggleStatus = async (slug) => {
    try {
      await blogService.toggleStatus(slug);
      setBlogs(prev => prev.map(item =>
        item.slug === slug
          ? { ...item, status: item.status === 'published' ? 'draft' : 'published' }
          : item
      ));
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await blogService.delete(deleteModal.slug);
      toast.success('Blog deleted successfully');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to delete blog');
    } finally {
      setDeleteLoading(false);
      setDeleteModal({ open: false, slug: null });
    }
  };

  const columns = [
    {
      header: 'Title',
      accessor: 'title',
      render: (row) => (
        <div className="news-title-cell">
          {row.coverImage && (
            <img src={row.coverImage} alt="" className="news-thumbnail" />
          )}
          <div className="news-title-content">
            <span className="news-title">{truncate(row.title, 50)}</span>
            <span className="news-date">{formatDate(row.publishDate)}</span>
          </div>
        </div>
      )
    },
    {
      header: 'Category',
      accessor: 'category',
      width: '120px',
      render: (row) => (
        <Badge variant="primary">{row.category}</Badge>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      width: '100px',
      render: (row) => (
        <Toggle
          name={`status-${row.slug}`}
          checked={row.status === 'published'}
          onChange={() => handleToggleStatus(row.slug)}
          size="small"
        />
      )
    },
    {
      header: 'State',
      width: '100px',
      render: (row) => (
        <Badge variant={row.status === 'published' ? 'success' : 'warning'}>
          {row.status === 'published' ? 'Published' : 'Draft'}
        </Badge>
      )
    },
    {
      header: 'Actions',
      width: '120px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button
            className="action-btn view"
            title="View"
            onClick={() => window.open(`https://designinfra.co.in/blog/${row.slug}`, '_blank')}
          >
            <FiEye />
          </button>
          <button
            className="action-btn edit"
            title="Edit"
            onClick={() => navigate(`/blogs/edit/${row.slug}`)}
          >
            <FiEdit2 />
          </button>
          <button
            className="action-btn delete"
            title="Delete"
            onClick={() => setDeleteModal({ open: true, slug: row.slug })}
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="news-page">
      <Card
        title="All Blogs"
        subtitle={`${totalItems} blog posts`}
        action={
          <Link to="/blogs/add">
            <Button icon={<FiPlus />}>Add Blog</Button>
          </Link>
        }
      >
        <DataTable
          columns={columns}
          data={blogs}
          loading={loading}
          searchable
          searchPlaceholder="Search blogs..."
          searchValue={search}
          onSearch={handleSearch}
          pagination
          pageSize={pageSize}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          exportable
          exportFilename="blogs-export"
          emptyMessage="No blogs found"
          actions={
            <Select
              name="category"
              value={categoryFilter}
              onChange={handleCategoryChange}
              options={categories}
              placeholder="All Categories"
            />
          }
        />
      </Card>

      <ConfirmDialog
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, slug: null })}
        onConfirm={handleDelete}
        title="Delete Blog"
        message="Are you sure you want to delete this blog post? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default BlogListPage;
